export interface SpeechRequest {
  text: string;
  language: string;
  rate: number;
  pitch: number;
  volume: number;
  voice?: SpeechSynthesisVoice;
  onBoundary?: (charIndex: number) => void;
  onEnd?: () => void;
}

export interface RecognitionResult {
  transcript: string;
  confidence: number;
}

interface BrowserRecognitionResultEvent extends Event {
  results: { 0?: { 0?: RecognitionResult } };
}

interface BrowserRecognition extends EventTarget {
  lang: string;
  interimResults: boolean;
  start(): void;
  onresult: ((event: BrowserRecognitionResultEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}

type RecognitionConstructor = new () => BrowserRecognition;
type SpeechWindow = Window & {
  SpeechRecognition?: RecognitionConstructor;
  webkitSpeechRecognition?: RecognitionConstructor;
};

export class BrowserSpeechService {
  voices(): SpeechSynthesisVoice[] {
    return "speechSynthesis" in window ? window.speechSynthesis.getVoices() : [];
  }

  onVoicesChanged(callback: () => void, signal: AbortSignal): void {
    if ("speechSynthesis" in window) window.speechSynthesis.addEventListener("voiceschanged", callback, { signal });
  }

  stop(): void {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }

  speak(request: SpeechRequest): void {
    if (!("speechSynthesis" in window)) return;
    this.stop();
    const utterance = new SpeechSynthesisUtterance(request.text);
    utterance.lang = request.language;
    utterance.rate = request.rate;
    utterance.pitch = request.pitch;
    utterance.volume = request.volume;
    utterance.voice = request.voice ?? null;
    utterance.onboundary = request.onBoundary ? (event) => {
      if (!event.name || event.name === "word") request.onBoundary?.(event.charIndex);
    } : null;
    utterance.onend = request.onEnd ?? null;
    window.speechSynthesis.speak(utterance);
  }

  canRecognize(): boolean {
    return Boolean(this.recognitionConstructor());
  }

  recognize(language: string, onResult: (result: RecognitionResult) => void, onError: () => void, onEnd: () => void): boolean {
    const Recognition = this.recognitionConstructor();
    if (!Recognition) return false;
    const recognition = new Recognition();
    recognition.lang = language;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const result = event.results[0]?.[0];
      if (result) onResult(result);
    };
    recognition.onerror = onError;
    recognition.onend = onEnd;
    recognition.start();
    return true;
  }

  private recognitionConstructor(): RecognitionConstructor | undefined {
    const browserWindow = window as SpeechWindow;
    return browserWindow.SpeechRecognition ?? browserWindow.webkitSpeechRecognition;
  }
}

export const browserSpeech = new BrowserSpeechService();
